import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export const DUPLICATE_BEER_WARNING_MESSAGE =
  'A possible duplicate match was found. Please review the matched beer(s) below. '
  + 'You can go back to edit the beer, reuse the existing beer at your selected locations, or use Add anyway to create this beer despite the match.';

export const GROUP_DUPLICATE_BEER_WARNING_MESSAGE =
  'This beer already exists elsewhere in your brewery group. Reusing it keeps existing ratings and reviews. '
  + 'You can add it to your selected locations or continue with a different beer.';

function formatAbvForDisplay(abv) {
  if (abv == null || abv === '') {
    return '—';
  }
  const numeric = Number(abv);
  if (Number.isNaN(numeric)) {
    return String(abv);
  }
  return String(numeric);
}

function formatDescriptionForDisplay(description) {
  const trimmed = typeof description === 'string' ? description.trim() : '';
  return trimmed.length > 0 ? trimmed : 'No description';
}

function getExistingBeerHeading(sourceType) {
  return sourceType === 'brewhouse_user'
    ? 'Existing beer in the Pint Portfolio'
    : 'Existing beer';
}

function duplicateBeerRowKey(entry) {
  const matched = entry.matchedBeer ?? {};
  const inputBeer = entry.inputBeer ?? {};
  return (
    [entry.beerDuplicateReviewId, matched.id, matched.name, inputBeer.name]
      .filter(Boolean)
      .join('|') || 'duplicate-row'
  );
}

function buildMatchMetaLine(entry) {
  return [
    entry.matchType,
    typeof entry.confidence === 'number' ? `Confidence: ${entry.confidence}` : null,
    entry.rationale,
  ]
    .filter(Boolean)
    .join(' · ');
}

function BeerComparisonField({ label, value, multiline = false }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={
          multiline
            ? {
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
            : undefined
        }
      >
        {value}
      </Typography>
    </Box>
  );
}

BeerComparisonField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  multiline: PropTypes.bool,
};

BeerComparisonField.defaultProps = {
  multiline: false,
};

function BeerSummaryCard({ title, name, styleLabel, abv, description, metaLine }) {
  const metaContent = metaLine ? (
    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, mb: 1 }}>
      {metaLine}
    </Typography>
  ) : null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {metaContent}
      <Stack spacing={1.25}>
        <BeerComparisonField label="Name" value={name || '—'} />
        <BeerComparisonField label="Style" value={styleLabel || '—'} />
        <BeerComparisonField label="ABV" value={formatAbvForDisplay(abv)} />
        <BeerComparisonField
          label="Description"
          value={formatDescriptionForDisplay(description)}
          multiline
        />
      </Stack>
    </Paper>
  );
}

BeerSummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  name: PropTypes.string,
  styleLabel: PropTypes.string,
  abv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  description: PropTypes.string,
  metaLine: PropTypes.string,
};

BeerSummaryCard.defaultProps = {
  name: '',
  styleLabel: '',
  abv: null,
  description: '',
  metaLine: null,
};

function DuplicateBeerComparison({ pendingBeer, duplicatedBeers, styleNameById, sourceType }) {
  const existingBeerHeading = getExistingBeerHeading(sourceType);

  return (
    <Stack spacing={2}>
      <BeerSummaryCard
        title="New beer"
        name={pendingBeer.name}
        styleLabel={pendingBeer.styleLabel}
        abv={pendingBeer.abv}
        description={pendingBeer.description}
      />
      {duplicatedBeers.map((entry) => {
        const matched = entry.matchedBeer ?? {};
        const styleKey = matched.style;
        const matchedStyleLabel =
          (styleKey && styleNameById[styleKey]) || styleKey || '—';

        return (
          <BeerSummaryCard
            key={duplicateBeerRowKey(entry)}
            title={existingBeerHeading}
            name={matched.name}
            styleLabel={matchedStyleLabel}
            abv={matched.abv}
            description={matched.description}
            metaLine={buildMatchMetaLine(entry)}
          />
        );
      })}
    </Stack>
  );
}

DuplicateBeerComparison.propTypes = {
  pendingBeer: PropTypes.shape({
    name: PropTypes.string,
    styleLabel: PropTypes.string,
    abv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
  }).isRequired,
  duplicatedBeers: PropTypes.arrayOf(PropTypes.object).isRequired,
  styleNameById: PropTypes.objectOf(PropTypes.string).isRequired,
  sourceType: PropTypes.string,
};

DuplicateBeerComparison.defaultProps = {
  sourceType: 'engage_user',
};

export function DuplicateAddBeerResults({
  pendingBeer,
  duplicatedBeers,
  styleNameById,
  sourceType,
}) {
  const isGroupDuplicate = duplicatedBeers.some(
    (entry) => entry?.rationale === 'exact group name match',
  );
  const warningMessage = isGroupDuplicate
    ? GROUP_DUPLICATE_BEER_WARNING_MESSAGE
    : DUPLICATE_BEER_WARNING_MESSAGE;

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Alert severity="warning">{warningMessage}</Alert>
      <DuplicateBeerComparison
        pendingBeer={pendingBeer}
        duplicatedBeers={duplicatedBeers}
        styleNameById={styleNameById}
        sourceType={sourceType}
      />
    </Stack>
  );
}

DuplicateAddBeerResults.propTypes = {
  pendingBeer: PropTypes.shape({
    name: PropTypes.string,
    styleLabel: PropTypes.string,
    abv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
  }).isRequired,
  duplicatedBeers: PropTypes.arrayOf(PropTypes.object).isRequired,
  styleNameById: PropTypes.objectOf(PropTypes.string).isRequired,
  sourceType: PropTypes.string,
};

DuplicateAddBeerResults.defaultProps = {
  sourceType: 'engage_user',
};
